import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import PageLoder from '../../components/PageLoader';
import exportEvaluationToPDF from '../../components/ExportEvaluationPDF';
import "../../styles/ConsultantEvaluation.css";
import './compact-eval.css';

export default function ViewSelfEvaluation() {
     const { id } = useParams();
     const navigate = useNavigate();
     const location = useLocation();
     const baseUrl = process.env.REACT_APP_BASE_URL;
     const [loading, setLoading] = useState(true);
     const [evaluation, setEvaluation] = useState(null);
     const [details, setDetails] = useState([]);
     const [message, setMessage] = useState(null);
     const [fetchError, setFetchError] = useState(null);

     const quarterLabel = (q) => {
          if (!q) return '';
          switch (q.toString().toUpperCase()) {
               case 'Q1': return 'Jan - Mar';
               case 'Q2': return 'Apr - Jun';
               case 'Q3': return 'Jul - Sep';
               case 'Q4': return 'Oct - Dec';
               default: return q;
          }
     };

     useEffect(() => {
          // show success message passed via navigation state (one-time)
          if (location && location.state && location.state.message) {
               setMessage(location.state.message);
               try {
                    navigate(location.pathname, { replace: true, state: {} });
               } catch (e) {
                    // ignore
               }
          }

          let mounted = true;
          const fetchEval = async () => {
               setLoading(true);
               try {
                    const token = localStorage.getItem('token');
                    const headers = { Accept: 'application/json' };
                    if (token) headers.Authorization = `Bearer ${token}`;

                    const url = `${baseUrl}/getConsultantEvaluation/${id}`;
                    const res = await fetch(url, { method: 'GET', headers });
                    const json = await res.json();
                    console.debug('getConsultantEvaluation response:', json);
                    if (!mounted) return;

                    // Accept several shapes: { evaluation:..., details: [...] } or object or array
                    if (json && json.evaluation) {
                         setEvaluation(json.evaluation);
                         setDetails(Array.isArray(json.details) ? json.details : []);
                    } else if (Array.isArray(json) && json.length > 0) {
                         // maybe backend returns array of evaluations; pick first
                         setEvaluation(json[0]);
                         setDetails(json[0].details || []);
                    } else if (json && typeof json === 'object') {
                         setEvaluation(json);
                         setDetails(json.details || []);
                    } else {
                         setEvaluation(null);
                         setDetails([]);
                    }

                    // fetch questions to display task text when available
                    try {
                         const qUrl = `${baseUrl}/getConsultantEvaluationQuestions`;
                         const qres = await fetch(qUrl, { method: 'GET', headers });
                         const qjson = await qres.json();
                         if (Array.isArray(qjson)) {
                              const map = qjson.reduce((acc, q) => ({ ...acc, [String(q.id)]: q.question_text || q.question || '' }), {});
                              // merge question text into details where missing
                              setDetails(prev => (Array.isArray(prev) ? prev.map(d => ({ ...d, question_text: d.question_text || map[String(d.question_id)] || map[String(d.id)] || '' })) : prev));
                         }
                    } catch (err) {
                         // non-fatal
                         console.debug('Failed to fetch questions:', err);
                    }
               } catch (err) {
                    console.error(err);
                    setMessage({ type: 'error', text: 'Failed to load evaluation' });
                    // capture fetch-level error to show actionable hints in UI
                    setFetchError(err && err.message ? String(err.message) : String(err));
               } finally {
                    if (mounted) setLoading(false);
               }
          };
          fetchEval();
          return () => { mounted = false; };
     }, [baseUrl, id, location, navigate]);



     const exportToPDF = async () => {
          try {
               await exportEvaluationToPDF({ evaluation, details, id, baseUrl, publicUrl: process.env.PUBLIC_URL || '', logoImage: undefined, envFileUrl: process.env.REACT_APP_FILE_URL || '' });
          } catch (err) {
               console.debug('Export failed', err);
               setMessage({ type: 'error', text: 'Export failed' });
          }
     };



     if (loading) return <div className="text-center py-4"><PageLoder /></div>;

     if (!evaluation) return <div className="consultant-eval">No evaluation found.</div>;

     // compute total points (fallback to sum of details)
     const displayTotal = (evaluation && (evaluation.total_points !== undefined && evaluation.total_points !== null))
          ? evaluation.total_points
          : (Array.isArray(details) ? details.reduce((s, d) => s + (parseInt(d.points, 10) || 0), 0) : 0);
     const displayAdminTotal = (evaluation && (evaluation.total_admin_points !== undefined && evaluation.total_admin_points !== null))
          ? evaluation.total_admin_points
          : (Array.isArray(details) ? details.reduce((s, d) => s + (parseInt(d.admin_points, 10) || 0), 0) : 0);

     const formatDateTime = (s) => {
          if (!s) return null;
          try {
               let ds = String(s).trim();
               if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(ds)) ds = ds.replace(' ', 'T');
               const d = new Date(ds);
               if (!isNaN(d.getTime())) {
                    const dd = String(d.getDate()).padStart(2, '0');
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    const hh = String(d.getHours()).padStart(2, '0');
                    const min = String(d.getMinutes()).padStart(2, '0');
                    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
               }
          } catch (e) {
               // ignore
          }
          return s;
     };

     const formatDateOnly = (s) => {
          if (!s) return null;
          try {
               let ds = String(s).trim();
               if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(ds)) ds = ds.replace(' ', 'T');
               const d = new Date(ds);
               if (!isNaN(d.getTime())) {
                    const dd = String(d.getDate()).padStart(2, '0');
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    return `${dd}-${mm}-${yyyy}`;
               }
          } catch (e) {
               // ignore
          }
          return s;
     };

     const renderMaybeEmpty = (v) => {
          if (v === null || v === undefined) return '';
          const num = Number(v);
          if (!Number.isNaN(num) && num === 0) return '';
          const str = String(v).trim();
          if (str === '') return '';
          return v;
     };

     // normalize DOJ and VDC experience from multiple possible field names
     const dojRaw = evaluation && (evaluation.doj || evaluation.date_of_joining || evaluation.join_date || evaluation.joined_on || evaluation.joinedOn || '');
     const doj = formatDateOnly(dojRaw);
     const vdcRaw = evaluation && (evaluation.vdc_exp || evaluation.vdc_experience || evaluation.vdcExp || '');
     const vdcExp = vdcRaw || '';

     const submittedOnRaw = evaluation && (evaluation.submitted_on || evaluation.submitted_at || evaluation.submitted_date || evaluation.updated_at || evaluation.updated_dtm || evaluation.created_at || evaluation.created_dtm || '');
     const submittedOn = formatDateTime(submittedOnRaw);

     const createdOnRaw = evaluation && (evaluation.created_at || evaluation.created_dtm || evaluation.created_on || evaluation.createdDate || '');
     const createdOn = formatDateTime(createdOnRaw);
     const updatedOnRaw = evaluation && (evaluation.updated_at || evaluation.updated_dtm || evaluation.updated_on || evaluation.updatedDate || '');
     const updatedOn = formatDateTime(updatedOnRaw);

     // If submitted timestamp isn't provided, fall back to updatedOn (or createdOn) when record is submitted
     const displayedSubmittedOn = submittedOn || (evaluation && evaluation.is_submitted === 'Y' ? (updatedOn || createdOn) : null);

     return (
          <div className="consultant-eval compact">
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2>View Self Evaluation</h2>
                    <div>
                         {evaluation?.is_submitted !== 'Y' && (
                              <button className="btn btn-sm " onClick={() => navigate(`/edit-self-evaluations/${id}/edit`)} style={{ marginRight: 8 }}>Edit</button>
                         )}
                         <button className="btn btn-sm" onClick={() => navigate(`/consultant-evaluations`)} style={{ marginRight: 8 }}>List</button>
                         <button className="btn btn-sm" onClick={() => exportToPDF()} style={{ marginLeft: 8 }}>Export PDF</button>
                    </div>
               </div>
               <div className="eval-meta" style={{ marginBottom: 12 }}>
                    {/* consolidated header: show all important metadata */}
                    <span style={{ display: 'inline-block', marginRight: 18 }}><strong>Emp Code:</strong> {evaluation.emp_code || ''}</span>
                    <span style={{ display: 'inline-block', marginRight: 18 }}><strong>Name:</strong> {evaluation.emp_name || evaluation.comp_name || ''}</span>
                    <span style={{ display: 'inline-block', marginRight: 18 }}><strong>Centre:</strong> {evaluation.centre || ''}</span>
                    <span style={{ display: 'inline-block', marginRight: 18 }}><strong>Specialty:</strong> {evaluation.specialty || ''}</span>
                    <span style={{ display: 'inline-block', marginRight: 18 }}><strong>Timing:</strong> {evaluation.timing || ''}</span>
                    <span style={{ display: 'inline-block', marginRight: 18 }}><strong>Mobile:</strong> {evaluation.mobile || ''}</span>
                    <span style={{ display: 'inline-block', marginRight: 18 }}><strong>Employment Type:</strong> {evaluation.employment_type || ''}</span>
                    <span style={{ display: 'inline-block', marginRight: 18 }}><strong>DOJ:</strong> {doj || ''}</span>
                    <span style={{ display: 'inline-block', marginRight: 18 }}><strong>VDC Exp:</strong> {vdcExp}</span>
                    <span style={{ display: 'inline-block', marginRight: 18 }}><strong>Department:</strong> {evaluation.dept || ''}</span>
                    <span style={{ display: 'inline-block', marginRight: 18 }}><strong>Submitted:</strong> {evaluation.is_submitted === 'Y' ? 'Yes' : 'No'}{displayedSubmittedOn ? `, on ${displayedSubmittedOn}` : ''}</span>
                    {createdOn && <span style={{ display: 'inline-block', marginLeft: 12 }}><strong>Created on:</strong> {createdOn}</span>}
                    {updatedOn && <span style={{ display: 'inline-block', marginLeft: 12 }}><strong>Updated on:</strong> {updatedOn}</span>}
                    <span style={{ display: 'inline-block', marginLeft: 12 }}><strong>Total Points:</strong> {renderMaybeEmpty(displayTotal)}</span>
                    <span style={{ display: 'inline-block', marginLeft: 12 }}><strong>Quarter:</strong> {quarterLabel(evaluation.quarter)}</span>
                    <span style={{ display: 'inline-block', marginLeft: 12 }}><strong>Year:</strong> {evaluation.year}</span>

                    {message && message.type === 'error' && (
                         <div style={{ padding: 10, border: '1px solid #e0b4b4', background: '#fff6f6', color: '#9f3a38', marginBottom: 12 }}>
                              <strong>Error:</strong> {message.text}
                              <div style={{ marginTop: 8 }}>
                                   Possible causes: CORS blocked request or backend returned an error (500). Check browser console/network for details.
                              </div>
                              <div style={{ marginTop: 8 }}>
                                   Quick fixes:
                                   <ul style={{ marginTop: 6 }}>
                                        <li>Add CORS headers on the backend (e.g. <em>Access-Control-Allow-Origin</em>).</li>
                                        <li>Or configure a dev proxy in React's <strong>package.json</strong> (e.g. <code>"proxy": "http://localhost"</code>).</li>
                                        <li>Or run the frontend from same origin as backend to avoid cross-origin requests.</li>
                                   </ul>
                              </div>
                              {fetchError && (
                                   <div style={{ marginTop: 8, fontSize: 12, color: '#5f2b2b' }}>
                                        <strong>Fetch error:</strong> {fetchError}
                                   </div>
                              )}
                         </div>
                    )}
               </div>

               <table className="eval-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                         <tr>
                              <th style={{ width: 60 }}>Sr. No</th>
                              <th style={{ width: 420 }}>Task</th>
                              <th style={{ width: 100 }}>Self Points</th>
                              <th style={{ width: 100 }}>HOD Points</th>
                              <th style={{ width: 240 }}>Self Comments</th>
                              <th style={{ width: 240 }}>HOD's Comments</th>
                         </tr>
                    </thead>
                    <tbody>
                         {details && details.length === 0 && <tr><td colSpan={4}>No tasks/details available.</td></tr>}
                         {details && details.map((d, idx) => (
                              <tr key={d.id || d.question_id || idx}>
                                   <td>{idx + 1}</td>
                                   <td>{d.question_text || d.question || d.question_label || d.question_id}</td>
                                   <td>{renderMaybeEmpty(d.points)}</td>
                                   <td>{renderMaybeEmpty(d.admin_points)}</td>
                                   <td>{d.comments}</td>
                                   <td>{d.admin_comments}</td>
                              </tr>
                         ))}
                         <tr style={{ fontWeight: 700 }}>
                              <td></td>
                              <td style={{ textAlign: 'right' }}>Total Points ( 45 )</td>
                              {/* <td>{renderMaybeEmpty(displayTotal)}</td>
                              <td>{renderMaybeEmpty(displayAdminTotal)}</td> */}
                              <td>Self Points {renderMaybeEmpty(displayTotal)}</td>
                              <td>HOD Points {renderMaybeEmpty(displayAdminTotal)}</td>
                              <td></td>
                              <td></td>
                         </tr>
                    </tbody>
               </table>

               {/* Overall remarks placed after the question table */}
               {evaluation.overall_comment && (
                    <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                         <div style={{ minWidth: 140, fontWeight: 700 }}>Overall Remarks:</div>
                         <div style={{ flex: 1, padding: 8, background: '#fafafa', border: '1px solid #eee' }}>{evaluation.overall_comment}</div>
                    </div>
               )}
          </div>
     );
}
